import type {
  StorefrontOrderHistoryDetail,
  StorefrontOrderHistoryItem,
  StorefrontPaymentAttemptStatus,
} from "@emynor/storefront-sdk";
import { storefrontSdk } from "./sdk";
import { ORDER_STATUSES } from "@emynor/storefront-sdk";
import type { CartLine, Order, OrderStatus } from "./types";
import { uiPaymentMethod } from "./types";

export async function listOrders(signal?: AbortSignal): Promise<Order[]> {
  const rows = await storefrontSdk.orders(signal ? { signal } : undefined);
  return rows.map(orderSummary);
}

export async function getOrder(
  id: string,
  signal?: AbortSignal,
): Promise<Order | undefined> {
  try {
    return orderDetail(
      await storefrontSdk.order(id, signal ? { signal } : undefined),
    );
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      error.status === 404
    )
      return undefined;
    throw error;
  }
}

export function orderPollingInterval(
  order: Pick<Order, "status"> | null | undefined,
): number | false {
  return order && ["completed", "rejected", "cancelled"].includes(order.status)
    ? false
    : 15_000;
}

export const statusTone: Record<OrderStatus, string> = {
  awaiting_payment: "bg-destructive/10 text-destructive border-destructive/40",
  pending: "bg-gold/25 text-gold-foreground border-gold/40",
  accepted: "bg-accent text-accent-foreground border-primary/25",
  rejected: "bg-destructive/12 text-destructive border-destructive/30",
  unknown: "bg-muted text-muted-foreground border-border",
  completed: "bg-success/15 text-success border-success/30",
  cancelled: "bg-destructive/12 text-destructive border-destructive/30",
};

export type OrderPaymentNoticeKind = "awaiting" | "failed" | "expired";

export function orderPaymentNoticeKind(
  order: Pick<Order, "status" | "paymentMethod" | "paymentStatus">,
  attemptStatus: StorefrontPaymentAttemptStatus | null = null,
): OrderPaymentNoticeKind | null {
  if (
    order.paymentMethod !== "online" ||
    ["paid", "partially_refunded", "refunded", "review_required"].includes(
      order.paymentStatus,
    )
  ) {
    return null;
  }
  if (order.paymentStatus === "expired" || attemptStatus === "expired") {
    return "expired";
  }
  if (attemptStatus === "failed" || attemptStatus === "cancelled") {
    return "failed";
  }
  return order.status === "awaiting_payment" ? "awaiting" : null;
}

export function orderCustomerPaidAmount(order: Order | null | undefined): number | null {
  if (!order?.raw || order.paymentMethod !== "online") return null;
  const summary = order.raw.payment_summary;
  if (
    orderPaymentNoticeKind(order, summary.attemptStatus) ||
    (summary.attemptStatus !== "succeeded" &&
      !["paid", "partially_refunded", "refunded"].includes(order.paymentStatus))
  ) {
    return null;
  }
  return summary.customerPaidAmountMinor;
}

export function orderSummary(row: StorefrontOrderHistoryItem): Order {
  return {
    id: row.id,
    number: row.order_number,
    status: normalizeStatus(row.status),
    createdAt: row.requested_at,
    type: row.order_type,
    branchId: "",
    lines: [],
    subtotal: row.subtotal_minor,
    deliveryFee: row.delivery_fee_minor,
    serviceFee: row.service_fee_minor,
    discount: row.discount_minor,
    tax: row.tax_minor,
    total: row.total_minor,
    currency: row.currency,
    paymentMethod: uiPaymentMethod(row.payment_method),
    paymentStatus: normalizePaymentStatus(row.payment_status),
    promotions: row.promotions.map((promotion) => ({
      id: promotion.id,
      name: localized(promotion.name_i18n),
      savings: promotion.discount_minor,
    })),
    loyalty: row.loyalty,
    ...(row.scheduled_for ? { pickupTimeISO: row.scheduled_for } : {}),
  };
}

export function orderDetail(row: StorefrontOrderHistoryDetail): Order {
  const summary = orderSummary(row);
  return {
    ...summary,
    paymentMethod: row.payment_summary.method,
    paymentStatus: normalizePaymentStatus(row.payment_summary.status),
    branchId: row.branch_id,
    lines: row.items.map<CartLine>((item) => {
      const additions = item.addons.map((addon) => ({
        id: addon.addon_id ?? addon.id,
        groupId: "",
        name: localized(addon.addon_name_i18n),
        price: addon.price_minor,
      }));
      return {
        id: item.id,
        itemId: item.menu_item_id ?? item.id,
        slug: "",
        name: localized(item.menu_item_name_i18n),
        image: item.image_url ?? "",
        ...(item.variant_id ? { variationId: item.variant_id } : {}),
        ...(item.variant_label_i18n
          ? { variationName: localized(item.variant_label_i18n) }
          : {}),
        unitPrice: Math.max(
          0,
          item.unit_price_minor -
            additions.reduce((sum, addon) => sum + addon.price, 0),
        ),
        originalUnitPrice: item.original_unit_price_minor ?? null,
        additions,
        ...(item.notes ? { note: item.notes } : {}),
        quantity: item.quantity,
      };
    }),
    ...(row.order_type === "delivery" && row.delivery_address_json
      ? {
          deliveryAddress: addressFromJson(
            row.delivery_address_json,
            row.customer_name,
            row.customer_phone_e164,
          ),
        }
      : {}),
    ...(row.order_type === "pickup"
      ? { recipientName: row.customer_name }
      : {}),
    ...(row.rejection_reason
      ? { cancellationReason: row.rejection_reason }
      : {}),
    raw: row,
  };
}

export function normalizeStatus(status: string): OrderStatus {
  return ORDER_STATUSES.includes(status as Exclude<OrderStatus, "unknown">)
    ? (status as OrderStatus)
    : "unknown";
}

function normalizePaymentStatus(status: string): Order["paymentStatus"] {
  switch (status) {
    case "unpaid":
    case "paid":
    case "processing":
    case "partially_refunded":
    case "refunded":
    case "review_required":
    case "expired":
      return status;
    default:
      return "unknown";
  }
}

function localized(value: Record<string, string>): { en: string; ar: string } {
  return {
    en: value["en"] ?? value["ar"] ?? "",
    ar: value["ar"] ?? value["en"] ?? "",
  };
}

function addressFromJson(
  value: Record<string, unknown>,
  fullName: string,
  phone: string,
) {
  return {
    id: "order-address",
    label: { en: "Delivery", ar: "التوصيل" },
    fullName,
    phone,
    street: String(value["street"] ?? ""),
    area: String(value["area"] ?? ""),
    building: String(value["building"] ?? ""),
    floor: String(value["floor"] ?? ""),
    apartment: String(value["apartment"] ?? ""),
    landmark: String(value["landmark"] ?? ""),
    note: String(value["instructions"] ?? ""),
    lat: typeof value["lat"] === "number" ? value["lat"] : NaN,
    lng: typeof value["lng"] === "number" ? value["lng"] : NaN,
  };
}
